import styled, {
  breakpoints,
  css,
  Device,
  IDataAttributeMap,
  mapWithDataPrefix,
  StyledComponent,
  useDeviceViewport,
  withTheme,
} from '@se-admin/falcon';

import * as React from 'react';

import { ISaleCardProps, SaleCardVariant } from './SaleCard.interfaces';

import { TranslationsContext } from '../../common/contexts';
import { ApplicationContext } from '../../common/contexts/ApplicationContext';
import { TrackingContext } from '../../common/contexts/TrackingContext';
import { Color } from '../../common/types/color';
import { getDateDifferenceInDays } from '../../common/utils/dateUtils';
import {
  IContentElementProps,
  InteractionType,
  ItemDetailsType,
  trackContentInteraction,
} from '../../common/utils/tracking';
import { useContentViewedTracking } from '../../common/utils/tracking/trackingUtils';
import {
  ImageLabelContainer,
  SaleCardAndHeartWrapper,
  SaleCardContent,
  SaleCardLink,
  SaleCardWrapper,
} from './SaleCard.styles';
import { renderContent, renderHeartIcon, renderSaleImage } from './SaleCard.utils';

interface ICustomSaleCardWrapperProps {
  daysToEnd: number;
  variant: SaleCardVariant;
}

const CustomSaleCardWrapper: StyledComponent<
  typeof SaleCardWrapper,
  {},
  ICustomSaleCardWrapperProps
> = styled(SaleCardWrapper)(
  ({ daysToEnd, variant }: ICustomSaleCardWrapperProps) => css`
    ${daysToEnd <= 0 &&
      variant !== SaleCardVariant.small &&
      css`
        @media only screen and (min-width: ${breakpoints[Device.tablet]}) {
          border: 1px solid ${Color.red};
        }
      `}
  `
);

/**
 * The Sale Card.
 */
const SaleCard: React.FunctionComponent<ISaleCardProps> = props => {
  const {
    className,
    dataAttributes,
    sale,
    variant,
    baseUrl,
    favouriteSales,
    userId,
    affiliateDetails,
    trackingPosition,
    onSizeChange,
    showTooltip,
    theme,
    trackPositionOnClick,
  } = props;
  const endDate: Date = new Date(`${sale.dates.end}`);
  const trackingId: IContentElementProps = React.useContext(TrackingContext);
  const daysToEnd: number = getDateDifferenceInDays(endDate, new Date());

  const dataAttrs: IDataAttributeMap = mapWithDataPrefix(dataAttributes);
  const { translations } = React.useContext(TranslationsContext);
  const { territoryConfig } = React.useContext(ApplicationContext);
  const isFlightFunctionalityEnabled: boolean = territoryConfig.isFlightFunctionalityEnabled;
  const device: Device = useDeviceViewport();

  const handleClick: () => void = () => {
    if (trackPositionOnClick) {
      history.replaceState({ trackingPosition }, undefined);
    }

    trackContentInteraction(trackingId, InteractionType.CLICK, {
      listPosition: trackingPosition,
      saleId: sale.id,
      saleType: sale.type,
    });
  };

  const containerRef: React.MutableRefObject<HTMLDivElement> = React.useRef();

  const itemDetails: ItemDetailsType = React.useMemo(
    () => ({
      listPosition: trackingPosition,
      saleId: sale.id,
      saleType: sale.type,
    }),
    [trackingPosition, sale.id, sale.type]
  );

  if (containerRef && trackingId && itemDetails) {
    useContentViewedTracking(containerRef, trackingId, itemDetails);
  }

  return (
    <SaleCardAndHeartWrapper ref={containerRef} data-saleid={sale.id}>
      {renderHeartIcon(sale, variant, baseUrl, favouriteSales)}
      <SaleCardLink onClick={handleClick} href={sale.links.sale}>
        <CustomSaleCardWrapper
          className={className}
          daysToEnd={daysToEnd}
          variant={variant}
          {...dataAttrs}
        >
          <ImageLabelContainer variant={variant}>
            {renderSaleImage(
              sale,
              variant,
              isFlightFunctionalityEnabled,
              territoryConfig,
              translations,
              onSizeChange,
              device
            )}
          </ImageLabelContainer>

          {variant === SaleCardVariant.normal ? (
            renderContent(
              sale,
              variant,
              userId,
              affiliateDetails,
              daysToEnd,
              endDate,
              territoryConfig,
              isFlightFunctionalityEnabled,
              translations,
              showTooltip,
              theme
            )
          ) : (
            <SaleCardContent>
              {renderContent(
                sale,
                variant,
                userId,
                affiliateDetails,
                daysToEnd,
                endDate,
                territoryConfig,
                isFlightFunctionalityEnabled,
                translations,
                showTooltip,
                theme
              )}
            </SaleCardContent>
          )}
        </CustomSaleCardWrapper>
      </SaleCardLink>
    </SaleCardAndHeartWrapper>
  );
};

SaleCard.defaultProps = {
  variant: SaleCardVariant.normal,
};

export default withTheme(SaleCard);
